<template>
  <LoadingSpinner :loading='loading'> </LoadingSpinner>

  <div class="sb-page">
    <ConfirmDialogue ref="confirmDialogue"></ConfirmDialogue>
    <div class="d-flex justify-content-between align-items-start flex-wrap" style="gap:1rem">
      <!-- No heading and no blurb: the tab above names this half and SiteEditor carries the one-line
           explanation, so both would be said twice within 60 pixels of each other. -->
      <div>
        <small class="text-muted">
          {{ statusText }}
          <a v-if="publishedVersion && storefrontUrl" :href="storefrontUrl" target="_blank"
             rel="noopener noreferrer" class="ms-1">Se den publiserte nettsiden ↗</a>
        </small>
      </div>
      <!-- The status sits on its OWN line under the buttons, not beside them. Beside them it changed the
           group's width every time a preset was clicked, and the header row wraps — so the whole panel
           dropped to the next line, left-aligned, and jumped back 2.5 s later when the message cleared.
           Always rendered, never v-if: the line is reserved whether or not there is a message, so an
           appearing status cannot shift the buttons vertically either. -->
      <div class="sb-actions">
        <div class="sb-actions__row">
          <!--
            The DRAFT, full size — the same button, wording and channel as the Hjemmeside tab. The pane
            beside these controls is a small mirror of the ordering page; this is the page itself.
          -->
          <button type="button" class="bo_btn_secondary" :disabled="saving || !location || !storefrontUrl"
                  @click="openPreview">
            <font-awesome-icon :icon="['fas', 'external-link-alt']" /> Forhåndsvis
          </button>
          <span v-if="previewBlocked" class="sb-actions__blocked">
            Nettleseren blokkerte vinduet — tillat sprettoppvinduer for denne siden.
          </span>

          <button ref="versionsButton" type="button" class="bo_btn_secondary" :disabled="saving || !location"
                  :aria-expanded="showVersions ? 'true' : 'false'" @click="showVersions = !showVersions">
            <font-awesome-icon :icon="['fas', 'history']" /> Tidligere versjoner
          </button>

          <button class="bo_btn_secondary" :disabled="saving || !location || !publishedVersion" @click="discardDraft">
            Forkast endringer
          </button>
          <button class="bo_btn_success" :disabled="saving || !location" @click="publish">
            {{ saving ? 'Publiserer…' : 'Publiser' }}
          </button>

          <!-- An overlay hung off its own button, like the Hjemmeside tab. In the rail it pushed the
               controls below it down, and the history holds twenty entries. -->
          <div v-if="showVersions" ref="versions" class="sb-versions">
            <h6 class="sb-versions__title">Tidligere versjoner</h6>
            <p class="sb-versions__hint">
              Å hente tilbake en versjon legger den i utkastet ditt. Den live nettsiden endres ikke før
              du publiserer.
            </p>
            <p v-if="!versions.length" class="sb-versions__hint mb-0">Ingen publiserte versjoner ennå.</p>
            <ul v-else class="sb-versions__list">
              <li v-for="v in versions" :key="v.version">
                <span class="sb-versions__no">Versjon {{ v.version }}</span>
                <span class="sb-versions__when">{{ formatTime(v.modifiedTime) }}</span>
                <button type="button" class="bo_btn_secondary" @click="restoreVersion(v)">Hent tilbake</button>
              </li>
            </ul>
          </div>
        </div>
        <!-- One line, two jobs: a transient result («Publisert ✓») when there is one, otherwise the
             quiet autosave receipt. Never both, so the row's height cannot change. -->
        <div v-if="status" class="sb-actions__status" :style="{ color: statusError ? '#c00' : '#197d2b' }">{{ status }}</div>
        <div v-else class="sb-actions__status sb-actions__status--saved">{{ savedText }}</div>
      </div>
    </div>

    <div v-if="savedInfo && storefrontUrl" class="bb-published">
      <span><strong>Lagret!</strong> Endringene vises nå på nettsiden din.</span>
      <a :href="storefrontUrl" target="_blank" rel="noopener" class="bo_btn" @click="savedInfo = false">Åpne nettsiden ↗</a>
    </div>

    <div v-if="!loading && !locations.length" class="mt-4" style="color:var(--bo-muted)">
      Fant ingen avdelinger for denne merchanten.
    </div>

    <div v-else class="row mt-3">
      <!-- Controls -->
      <div class="col-lg-3">
        <div class="mb-3" v-if="locations.length > 1">
          <span class="ingredient-title">Avdeling</span>
          <select class="form-select" v-model="location" @change="onLocationChange">
            <option v-for="loc in locations" :key="loc.shortName" :value="loc.shortName">{{ loc.name }} ({{ loc.shortName }})</option>
          </select>
        </div>

        <div class="bb-card">
          <h6 class="bb-h">Oppsett</h6>
          <div class="bb-layouts">
            <button v-for="p in layouts" :key="p.key" type="button"
                    class="bb-layout" :class="{ active: p.key === activeLayoutKey }" @click="applyLayout(p)">
              <span class="bb-layout-diagram">
                <i v-for="s in p.sections" :key="s" class="bb-lrow" :class="'lr-' + s">{{ sectionShort(s) }}</i>
              </span>
              <span class="bb-preset-label">{{ p.label }}</span>
            </button>
          </div>
        </div>

        <div class="bb-card">
          <h6 class="bb-h">Menyvisning</h6>
          <div class="bb-layouts">
            <button v-for="m in menuLayouts" :key="m.key" type="button"
                    class="bb-layout" :class="{ active: m.key === schema.menuLayout }" @click="applyMenuLayout(m.key)">
              <span class="bb-mld" :class="'bb-mld-' + m.key"><i v-for="n in mldCells(m.key)" :key="n"></i></span>
              <span class="bb-preset-label">{{ m.label }}</span>
            </button>
          </div>
        </div>

        <!-- AI prompt → generate. Hidden in v1.0 (aiEnabled=false) until the vLLM/Qwen node is funded;
             kept intact for v2.0. Restore notes: docs/website-builder-ai-v2.md -->
        <div v-if="aiEnabled" class="bb-card">
          <h6 class="bb-h">Beskriv stilen</h6>
          <textarea class="form-control" rows="3" v-model="prompt"
                    placeholder="f.eks. moderne minimalistisk, svart &amp; hvitt, elegant"></textarea>
          <button class="bo_btn w-100 mt-2" :disabled="generating || !prompt.trim()" @click="generate">
            {{ generating ? 'Genererer…' : 'Generer med AI' }}
          </button>
          <div v-if="genNote" class="mt-2" style="font-size:0.85rem; color:var(--bo-ink-2)">{{ genNote }}</div>
        </div>

        <!-- The colour picker that used to live here is gone. It wrote to THIS location's schema while
             the homepage editor wrote to its own, so a merchant could give the two halves of one website
             different palettes and never see that they had. The look is set once, on the Hjemmeside tab,
             and publishing carries it here. -->
        <div class="bb-card bb-card--muted">
          <h6 class="bb-h">Utseende</h6>
          <p class="bb-note">
            Farger, skrift og hjørnerunding settes på <strong>Hjemmeside</strong>-fanen og gjelder hele
            nettstedet. Endringene dukker opp her når du publiserer hjemmesiden.
          </p>
        </div>

        <div class="bb-card">
          <h6 class="bb-h">Logo</h6>
          <!-- Per MERCHANT, not per location: the storefront header reads merchantSetting.logoImageFile
               on EVERY page, so one logo covers every avdeling. Said out loud because this page has a
               location selector and everything else on it is per-location. -->
          <div class="bb-sublabel">Vises øverst på nettsiden din — samme logo for alle avdelinger.</div>
          <div v-if="logoImage" class="bb-logo-preview"><img :src="logoImage" alt="Logo"></div>
          <div class="d-flex align-items-center flex-wrap" style="gap:0.5rem">
            <input ref="logoFile" type="file" class="form-control form-control-sm" style="max-width:280px"
                   accept="image/png,image/jpeg,image/webp,image/avif" @change="selectLogo">
            <button v-if="logoImage" class="bo_btn_secondary bb-btn-sm" :disabled="logoUploading"
                    @click="removeLogo">Fjern</button>
          </div>
          <div v-if="logoUploading" class="bb-hero-msg">Laster opp …</div>
          <div v-else-if="logoError" class="bb-hero-msg bb-hero-msg--error">{{ logoError }}</div>
          <div v-else-if="logoImage" class="bb-hero-msg bb-hero-msg--ok">Logo er lastet opp.</div>
          <div v-else class="bb-hero-msg">Uten logo vises navnet på restauranten.</div>
        </div>

        <div class="bb-card">
          <h6 class="bb-h">Topptekst (hero)</h6>
          <input class="form-control mb-2" v-model="schema.hero.headline" maxlength="80" placeholder="Overskrift">
          <input class="form-control mb-2" v-model="schema.hero.subheadline" maxlength="160" placeholder="Underoverskrift">
          <div class="bb-sublabel">Bannerbilde — {{ heroHint }}</div>
          <div class="d-flex align-items-center flex-wrap" style="gap:0.5rem">
            <input ref="heroFile" type="file" class="form-control form-control-sm" style="max-width:280px"
                   accept="image/png,image/jpeg,image/webp,image/avif" @change="selectHeroImage">
            <button v-if="hasOwnHeroImage" class="bo_btn_secondary bb-btn-sm" :disabled="heroUploading"
                    @click="removeHeroImage">Fjern</button>
          </div>
          <div v-if="heroUploading" class="bb-hero-msg">Laster opp …</div>
          <div v-else-if="heroError" class="bb-hero-msg bb-hero-msg--error">{{ heroError }}</div>
          <!-- hasOwnHeroImage, not hero.image: every new merchant is SEEDED with the system banner, so a
               non-empty URL said "lastet opp" on a first-ever visit and offered to remove a picture
               nobody had chosen. -->
          <div v-else-if="hasOwnHeroImage" class="bb-hero-msg bb-hero-msg--ok">Bannerbilde er lastet opp.</div>
          <div v-else class="bb-hero-msg">Uten eget bilde brukes standardbanneret.</div>
        </div>

        <div class="bb-card" v-if="schema.sections.includes('about')">
          <h6 class="bb-h">Om oss</h6>
          <input class="form-control mb-2" v-model="schema.about.heading" maxlength="60" placeholder="Overskrift (f.eks. Om oss)">
          <textarea class="form-control" rows="4" v-model="schema.about.body" maxlength="2000" placeholder="Fortell kort om restauranten…"></textarea>
        </div>

        <div class="bb-card">
          <h6 class="bb-h">Kart (Google Maps)</h6>
          <input class="form-control" v-model="schema.mapUrl" maxlength="600" placeholder="Google Maps embed-URL (https://www.google.com/maps/…)">
          <small class="text-muted d-block mt-1">Tom = kartet genereres automatisk fra adressen. Lim inn en egen «Embed a map»-URL for å overstyre.</small>
        </div>

        <div class="bb-card">
          <h6 class="bb-h">Seksjoner</h6>
          <ul class="bb-sections">
            <li v-for="(s, i) in schema.sections" :key="s">
              <span class="text-capitalize">{{ s }}<em v-if="isRequired(s)" style="color:var(--bo-muted); font-style:normal"> · påkrevd</em></span>
              <span>
                <button class="btn btn-sm btn-outline-secondary bb-mini" :disabled="i===0" @click="moveSection(i,-1)">↑</button>
                <button class="btn btn-sm btn-outline-secondary bb-mini" :disabled="i===schema.sections.length-1" @click="moveSection(i,1)">↓</button>
                <button class="btn btn-sm btn-outline-danger bb-mini" :disabled="isRequired(s)" @click="removeSection(i)">✕</button>
              </span>
            </li>
          </ul>
          <div v-if="missingSections.length" class="mt-2">
            <button v-for="s in missingSections" :key="s" class="btn btn-sm btn-outline-secondary me-1 text-capitalize" @click="addSection(s)">+ {{ s }}</button>
          </div>
        </div>

        <!-- «Nullstill» left the action row: it is rare and drastic, and the row is now the same four
             controls the Hjemmeside tab has. The capability stays, demoted to where it belongs. -->
        <div class="bb-card bb-card--muted">
          <h6 class="bb-h">Start på nytt</h6>
          <p class="bb-note">
            Setter oppsett, tekst og menyvisning tilbake til plattformens standard. Farger og skrift
            styres fra Hjemmeside-fanen.
          </p>
          <button type="button" class="bo_btn_secondary w-100 mt-2" @click="resetSchema">Nullstill</button>
        </div>
      </div>

      <!-- Live preview (mirrors the Storefront ordering layout) -->
      <div class="col-lg-9">
        <div class="text-muted fw-bold mb-1" style="font-size:0.8rem">{{ hasMenu ? 'Forhåndsvisning — din meny' : 'Forhåndsvisning — eksempeldata' }}</div>
        <div class="preview-frame" :style="previewVars">
          <template v-for="s in schema.sections" :key="s">
            <!-- HERO — full-bleed image banner (first dish photo as stand-in); hero copy overlaid if set -->
            <section v-if="s==='hero'" class="pv-hero"
                     :style="heroImage ? { backgroundImage: 'linear-gradient(180deg, rgba(0,0,0,.1), rgba(0,0,0,.55)), url(' + heroImage + ')' } : {}">
              <div class="pv-hero-text">
                <h1 v-if="schema.hero.headline">{{ schema.hero.headline }}</h1>
                <p v-if="schema.hero.subheadline">{{ schema.hero.subheadline }}</p>
              </div>
            </section>

            <!-- MENU — store-header chrome + category nav + dish grid (the standard ordering layout) -->
            <section v-else-if="s==='menu'" class="pv-menu" :class="'pv-menu--' + schema.menuLayout">
              <div class="pv-header">
                <div class="pv-logo"><img :src="previewLogo" alt="Logo" /></div>
                <div class="pv-store-block">
                  <div class="pv-store-name">{{ storeName }}</div>
                  <div class="pv-store-sub">
                    <svg class="pv-ico" viewBox="0 0 24 24"><circle cx="12" cy="12" r="9"/><path d="M12 7.5V12l3 1.8"/></svg>
                    <span class="pv-open">Åpen</span><span class="pv-dim"> – Stenger kl 21:00</span>
                  </div>
                </div>
                <div class="pv-contact">
                  <div class="pv-contact-row">
                    <svg class="pv-ico" viewBox="0 0 24 24"><path d="M6.5 4h3l1.5 4-2 1.5a11 11 0 0 0 5 5l1.5-2 4 1.5v3a2 2 0 0 1-2 2A15 15 0 0 1 4.5 6a2 2 0 0 1 2-2z"/></svg>
                    {{ storePhone }}
                  </div>
                  <div class="pv-contact-row">
                    <svg class="pv-ico" viewBox="0 0 24 24"><circle cx="12" cy="12" r="9"/><path d="M12 7.5V12l3 1.8"/></svg>
                    Tilberedningstid: {{ storePrep }}
                  </div>
                </div>
                <nav class="pv-topnav">
                  <span class="pv-tab active">
                    <svg class="pv-ico" viewBox="0 0 24 24"><path d="M5 3v5a2 2 0 0 0 4 0V3M7 11V3M7 11v10M17 3c-1.4 0-2.5 2-2.5 5s1.1 4 2.5 4v9"/></svg>
                    Meny
                  </span>
                  <span class="pv-tab">
                    <svg class="pv-ico" viewBox="0 0 24 24"><circle cx="12" cy="12" r="9"/><path d="M12 11v5M12 8h.01"/></svg>
                    Info
                  </span>
                  <span class="pv-tab">
                    <span class="pv-cart">
                      <svg class="pv-ico" viewBox="0 0 24 24"><circle cx="9" cy="20" r="1.3"/><circle cx="17" cy="20" r="1.3"/><path d="M3 4h2l2.2 11h10l2-7H6"/></svg>
                      <i class="pv-cart-badge">0</i>
                    </span>
                    Handlekurv
                  </span>
                </nav>
              </div>

              <nav class="pv-cats">
                <span v-for="c in previewCats" :key="c.key" class="pv-cat" :class="{ active: c.key === activeCatKey }" @click="setActiveCat(c.key)">{{ c.label }}</span>
              </nav>

              <div class="pv-menu-body">
                <div class="pv-menu-head">
                  <h2>{{ activeCatLabel }}</h2>
                  <label class="pv-allergen">Vis allergen
                    <span class="pv-switch" :class="{ on: previewShowAllergen }" @click="previewShowAllergen = !previewShowAllergen"><i></i></span>
                  </label>
                </div>
                <div class="pv-grid" :class="'pv-grid--' + schema.menuLayout">
                  <article v-for="m in previewItems" :key="m.key" class="pv-card">
                    <div class="pv-thumb" :style="m.img ? { backgroundImage: 'url(' + m.img + ')' } : {}"></div>
                    <div class="pv-card-body">
                      <h3>{{ m.name }}</h3>
                      <p v-if="m.desc" class="pv-muted">{{ m.desc }}</p>
                      <p v-if="m.allergens" class="pv-allergens">Inneholder: {{ m.allergens }}</p>
                      <span class="pv-price">{{ m.price }}kr</span>
                    </div>
                    <button class="pv-add" aria-label="Legg til">+</button>
                  </article>
                </div>
              </div>
            </section>

            <section v-else-if="s==='about'" class="pv-about">
              <h2>{{ schema.about.heading || 'Om oss' }}</h2>
              <p class="pv-muted" style="white-space:pre-line">{{ schema.about.body || 'Fortell kort om restauranten din her.' }}</p>
            </section>
          </template>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import LoadingSpinner from "@/components/other/LoadingSpinner";
import ConfirmDialogue from "@/components/other/ConfirmDialogue.vue";
import LocationService from "@/services/location/LocationService";
import ProductService from "@/services/product/ProductService";
import SiteThemeService from "@/services/site/SiteThemeService";
import MediaFileService from "@/services/media/MediaFileService";
import SettingsService from "@/services/settings/SettingsService";
import ImageCompressor from "@/util/ImageCompressor";
import SiteImageSlots from "@/util/SiteImageSlots";
import heroDefaultImage from "@/assets/images/builder/webshop_header_web.webp";
import webshopLogo from "@/assets/images/builder/webshop-logo.png";

/** Message namespace for the preview window. Distinct from the homepage's, so two open builders
 *  never read each other's post. */
const PREVIEW_MSG = 'cagista-ordering';

/** Same beat as the Hjemmeside tab: long enough to coalesce a drag, short enough to be invisible. */
const AUTOSAVE_MS = 900;

const ALLOWED_SECTIONS = ['hero', 'menu', 'about'];
const REQUIRED_SECTIONS = ['hero', 'menu'];
/*
 * The banner every new merchant is seeded with (MerchantOnboardingService writes it, and
 * SiteSchemaValidator.defaultSchema() defines it). It is a real URL rather than a blank, which is why
 * hero.image being non-empty does NOT mean the merchant uploaded anything — see hasOwnHeroImage.
 */
const SYSTEM_HERO_IMAGE = 'https://cagista-s3.s3.eu-north-1.amazonaws.com/system/webshop_header.webp';

function defaultSchema() {
  return {
    version: 1,
    theme: {
      colorBackground: '#160d08', colorSurface: '#1c130f', colorText: '#ffffff', colorMuted: '#bdbdbd',
      colorPrimary: '#f30b0c', colorOnPrimary: '#ffffff', colorBorder: '#3a2a22',
      fontFamily: 'Roboto', headingWeight: 700, radius: 8
    },
    hero: { headline: 'Bestill på nett', subheadline: 'God mat, laget med omtanke.', image: SYSTEM_HERO_IMAGE },
    about: { heading: 'Om oss', body: '' },
    mapUrl: '',
    menuLayout: 'cards',
    sections: ['hero', 'menu', 'about']
  };
}

const LAYOUT_PRESETS = [
  { key: 'standard', label: 'Standard', sections: ['hero', 'menu', 'about'] },
  { key: 'about-first', label: 'Om oss først', sections: ['hero', 'about', 'menu'] },
  { key: 'menu-only', label: 'Kun meny', sections: ['hero', 'menu'] }
];

// Menu display styles ("Menyvisning") — how dish components render. Mirrors SiteSchemaValidator's
// MENU_LAYOUTS; the Storefront restyles the same dish markup via a site-menu--<key> container class.
const MENU_LAYOUTS = [
  { key: 'cards', label: 'Klassisk' },
  { key: 'gallery', label: 'Galleri' },
  { key: 'list', label: 'Kompakt liste' },
  { key: 'sidebar', label: 'Sidemeny' }
];

function fontStack(f) {
  switch (f) {
    case 'Playfair Display': return "'Playfair Display', Georgia, serif";
    case 'Georgia': return "Georgia, 'Times New Roman', serif";
    case 'Roboto Mono': return "'Roboto Mono', ui-monospace, monospace";
    case 'Roboto': return "'Roboto', sans-serif";
    case 'Poppins': return "'Poppins', sans-serif";
    default: return "'Inter', sans-serif";
  }
}

export default {
  name: "SiteTheme",
  components: { LoadingSpinner, ConfirmDialogue },
  data() {
    return {
      // The banner is rendered full-bleed at a fixed aspect ratio by the Storefront header, so a picture
      // of any other shape is letterboxed or cropped by the browser and looks like our bug rather than a
      // wrong upload. Refuse it instead, and say the size.
      heroUploading: false,
      heroError: '',
      /*
       * A logo is chrome, not a banner: no fixed dimensions, just a cap so a 4 MB photo straight off a
       * phone does not end up on every page of the ordering site.
       */
      logoUploading: false,
      logoError: '',
      logoImage: '',
      /*
       * The WHOLE merchant_setting document as it was loaded, kept so save() can send it back intact.
       * editMerchantSetting UPSERTS whatever JSON it is handed — hand it a hand-built subset and every
       * field not in that subset is gone, useSaaSFeatures included. Only logoImageFile is ever changed
       * here; nothing else on this object is touched.
       */
      merchantSetting: null,
      logoAtLoad: '',
      loading: true,
      locations: [],
      location: '',
      schema: defaultSchema(),
      // v1.0: AI prompt→generate hidden until the vLLM/Qwen node is funded (v2.0). See
      // docs/website-builder-ai-v2.md. Merchants use the predefined Maler/Oppsett/Menyvisning presets.
      aiEnabled: false,
      prompt: '',
      generating: false,
      genNote: '',
      saving: false,
      status: '',
      statusError: false,
      savedInfo: false,
      publishedVersion: 0,
      previewBlocked: false,
      saveTimer: null,
      // Separate from `saving`, which is Publiser's busy flag: an autosave must not grey out the row.
      draftSaving: false,
      savedAt: null,
      // Set while a schema that CAME FROM THE SERVER is adopted, so the deep watch does not answer a
      // read with a write. See adoptSchema.
      suppressSave: false,
      storefrontBase: '',
      showVersions: false,
      layouts: LAYOUT_PRESETS,
      menuLayouts: MENU_LAYOUTS,
      mockStore: { name: 'Din restaurant', status: 'Åpen · stenger 21:00', prep: '15 min' },
      mockCategories: ['Populært', 'Maki ruller', 'Bowls', 'Familiepakker', 'Drikke', 'Tilbehør'],
      mockMenu: [
        { name: 'Margherita', desc: 'Tomat, mozzarella, basilikum', price: 169, allergens: 'hvete, melk' },
        { name: 'Laks poke', desc: 'Ris, edamame, avokado', price: 189, allergens: 'fisk, soya' },
        { name: 'Veggie bowl', desc: 'Sesongens grønt, tahini', price: 159, allergens: 'sesam' },
        { name: 'Cheeseburger', desc: 'Storfe, cheddar, pommes', price: 179, allergens: 'hvete, melk' },
        { name: 'Kyllingwok', desc: 'Nudler, wokgrønnsaker, soya', price: 175, allergens: 'hvete, soya' },
        { name: 'Sjokoladefondant', desc: 'Varm kjerne, vaniljeis', price: 99, allergens: 'hvete, melk, egg' },
        { name: 'Caesar salat', desc: 'Romanosalat, parmesan, krutonger', price: 149, allergens: 'hvete, melk, egg, fisk' },
        { name: 'Hvitløksbrød', desc: 'Sprøtt brød, hvitløkssmør, urter', price: 69, allergens: 'hvete, melk' }
      ],
      previewShowAllergen: false,
      // Real menu (merchant-wide; filtered to the selected location for the preview)
      categories: [],
      items: [],
      previewCategoryId: '',
      versions: []
    };
  },
  /**
   * The preview window is held HERE, not in data().
   *
   * Vue makes everything in `data()` deeply reactive, and a reactive proxy around a CROSS-ORIGIN
   * Window throws `SecurityError` on the first property read — the proxy probes `__v_isRef`, which the
   * browser refuses across origins. So `previewWindow.closed` threw, every post was swallowed, and
   * `event.source === this.previewWindow` compared a raw Window against a proxy and was false. The
   * preview opened and then quietly showed the published page.
   *
   * `markRaw()` is the usual escape and does not work either: it WRITES a marker onto the object,
   * which is refused across origins for the same reason. An instance property assigned outside
   * `data()` is never made reactive, which is what this needs — nothing renders from it.
   */
  created() {
    this.previewWindow = null;
  },
  watch: {
    /**
     * Every change to the schema does two things: it reaches an open preview window, and it is written
     * to the draft a moment later.
     *
     * <b>Autosave, because the alternative was losing the work.</b> This tab used to write nothing at
     * all until «Publiser» — `saveSiteSchema` was never called — so a refresh threw the session away
     * without warning, and «Forkast endringer» offered to discard a draft the page could not create.
     * The Hjemmeside tab has always autosaved; these are two halves of one editor and a merchant has
     * no reason to expect them to differ.
     *
     * The preview is posted unconditionally, the save is not: adopting a schema the server just
     * handed us must not send it straight back. See adoptSchema.
     */
    schema: {
      deep: true,
      handler() {
        this.postToPreview();
        this.queueSave();
      }
    }
  },
  computed: {
    /** True only for a banner THIS merchant uploaded — the seeded system default does not count. */
    hasOwnHeroImage() {
      const img = (this.schema.hero && this.schema.hero.image) || '';
      return !!img && img !== SYSTEM_HERO_IMAGE;
    },

    previewVars() {
      const t = this.schema.theme;
      return {
        '--bg': t.colorBackground, '--surface': t.colorSurface, '--text': t.colorText, '--muted': t.colorMuted,
        '--primary': t.colorPrimary, '--on-primary': t.colorOnPrimary, '--border': t.colorBorder,
        '--radius': t.radius + 'px', '--font': fontStack(t.fontFamily), '--hw': String(t.headingWeight)
      };
    },
    missingSections() {
      return ALLOWED_SECTIONS.filter((s) => !this.schema.sections.includes(s));
    },
    // Link to the merchant's live Storefront ordering site for the selected location (multitenant by
    // subdomain: <merchant>.<host>/<locationShortName>).
    /**
     * <b>The host comes from the server.</b> This used to compose it here from the slug and the build
     * mode, so it never honoured a merchant's own domain — the same «Se live nettsiden» button pointed
     * at two different hosts on the two tabs of one editor. CustomDomainService is the one place that
     * composes it, and it prefers their domain once that is live.
     */
    storefrontUrl() {
      if (!this.storefrontBase || !this.location) return '';
      return this.storefrontBase + '/' + this.location;
    },
    /** The origin the preview window is allowed to talk to — derived, never composed by hand. */
    previewOrigin() {
      try {
        return new URL(this.storefrontUrl).origin;
      } catch (e) {
        return '';
      }
    },
    heroHint() {
      return SiteImageSlots.hint(SiteImageSlots.SLOTS.orderingHero);
    },
    statusText() {
      if (this.loading) return 'Laster …';
      if (!this.publishedVersion) return 'Ikke publisert ennå — gjestene ser den ikke før du trykker Publiser.';
      return 'Publisert, versjon ' + this.publishedVersion + '. Endringer du gjør nå er et utkast.';
    },
    savedText() {
      if (this.draftSaving) return 'Lagrer …';
      return this.savedAt ? 'Utkast lagret ' + this.savedAt : '';
    },
    // Which preset (if any) the current theme matches — drives the active highlight in the Maler picker.
    // Which layout preset the current section arrangement matches — highlights it in the Oppsett picker.
    activeLayoutKey() {
      const cur = (this.schema.sections || []).join(',');
      const match = this.layouts.find((p) => p.sections.join(',') === cur);
      return match ? match.key : '';
    },
    // --- Real-menu preview (falls back to mock when the merchant/location has no menu) ---
    currentLocation() {
      return (this.locations || []).find((l) => l.shortName === this.location) || null;
    },
    locId() {
      return this.currentLocation ? this.currentLocation.id : '';
    },
    storeName() {
      const loc = this.currentLocation;
      return (loc && (loc.displayName || loc.name)) || 'Din restaurant';
    },
    availableCategories() {
      const items = this.items || [];
      return (this.categories || [])
        .filter((c) => this.availAt(c) && this.availForWeb(c)
          && items.some((it) => it.categoryId === c.id && this.availAt(it) && this.availForWeb(it)))
        .sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0));
    },
    hasMenu() {
      return this.availableCategories.length > 0;
    },
    activeCatId() {
      const cats = this.availableCategories;
      if (!cats.length) return '';
      const found = cats.find((c) => c.id === this.previewCategoryId);
      return found ? found.id : cats[0].id;
    },
    previewCats() {
      if (!this.hasMenu) return this.mockCategories.map((c, i) => ({ key: 'mock' + i, label: c }));
      return this.availableCategories.map((c) => ({ key: c.id, label: c.name }));
    },
    activeCatKey() {
      if (this.hasMenu) return this.activeCatId;
      const keys = this.previewCats.map((c) => c.key);
      return keys.indexOf(this.previewCategoryId) >= 0 ? this.previewCategoryId : (keys[0] || '');
    },
    previewItems() {
      if (!this.hasMenu) {
        return this.mockMenu.map((m, i) => ({ key: 'mock' + i, name: m.name, desc: m.desc, price: m.price, img: '', allergens: m.allergens || '' }));
      }
      const catId = this.activeCatId;
      return (this.items || [])
        .filter((it) => it.categoryId === catId && this.availAt(it) && this.availForWeb(it))
        .sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0))
        .slice(0, 12)
        .map((it) => ({
          key: it.id || it.name,
          name: it.name,
          desc: it.description || it.ingredientsText || '',
          price: Math.round((it.price || 0) / 100),
          img: it.imageUrl || '',
          allergens: (it.allergens || []).map((a) => a && a.name).filter(Boolean).join(', ')
        }));
    },
    // Hero banner image from the schema; fall back to the bundled default so the preview always shows one.
    heroImage() {
      return (this.schema.hero && this.schema.hero.image) || heroDefaultImage;
    },
    activeCatLabel() {
      const c = (this.previewCats || []).find((x) => x.key === this.activeCatKey);
      return c ? c.label : 'Meny';
    },
    // Default preview logo (the schema has no logo field yet).
    /**
     * What the PREVIEW shows: the merchant's own logo when they have uploaded one, the sample otherwise.
     *
     * Deliberately separate from `logoImage`, which is data and holds the uploaded URL or nothing. The two
     * uses pull in opposite directions — the preview always needs something to draw, while the upload
     * panel must be able to tell «none yet» from «uploaded». This used to be a computed CALLED logoImage
     * that always returned the sample, which shadowed the data property of the same name: the panel then
     * reported a logo was uploaded before anyone had uploaded one, and every assignment to this.logoImage
     * failed silently against a readonly computed. Same shape as heroImage/hasOwnHeroImage, which got it
     * right.
     */
    previewLogo() {
      return this.logoImage || webshopLogo;
    },
    storePhone() {
      const loc = this.currentLocation;
      return (loc && loc.phone) || '00 00 00 00';
    },
    storePrep() {
      const loc = this.currentLocation;
      const m = loc && loc.earliestPickupTime;
      return (m ? m : 15) + ' min';
    }
  },
  mounted() {
    document.addEventListener("click", this.onDocumentClick, true);
    document.addEventListener("keydown", this.onKeydown);
    window.addEventListener("message", this.onPreviewMessage);
    this.injectFonts();
    this.loadMenu();
    this.loadMerchantSetting();
    LocationService.listLocations().then(
      (res) => {
        this.locations = (res.data || []).filter((l) => l && l.shortName);
        if (this.locations.length) {
          this.location = this.locations[0].shortName;
          this.loadSchema();
        } else {
          this.loading = false;
        }
      },
      (err) => { console.log(err); this.loading = false; }
    );
  },
  beforeUnmount() {
    document.removeEventListener("click", this.onDocumentClick, true);
    document.removeEventListener("keydown", this.onKeydown);
    window.removeEventListener("message", this.onPreviewMessage);
    // A timer that fires after this component is gone writes a draft nobody is looking at — and on the
    // ordering tab the parent REMOUNTS this component when the homepage publishes a theme, so the
    // stale write would land on top of the theme that publish just pushed here.
    this.cancelQueuedSave();
  },
  methods: {
    /* ── bildeopplasting ────────────────────────────────────────────────────────── */

    /**
     * Judge a picked file before a byte of it is uploaded. Both uploads on this tab go through here,
     * and so does the Hjemmeside tab's — same rule, same wording, one place it is decided.
     *
     * Too small is refused outright: nothing can put back detail that is not in the file. A wrong
     * SHAPE is a question instead, because the image is good enough and the only issue is which part
     * of it survives the crop — a refusal there would be a dead end for a merchant holding one good
     * photograph in portrait.
     *
     * @returns true to go ahead
     */
    async acceptImage(file, slot, errorField){
      let size;
      try {
        size = await ImageCompressor.readDimensions(file);
      } catch (e) {
        this[errorField] = 'Kunne ikke lese bildet. Er det en gyldig bildefil?';
        return false;
      }
      const verdict = SiteImageSlots.inspect(size, slot, ImageCompressor.plan(size.width, size.height, slot));
      if(verdict.verdict === 'tooSmall'){
        this[errorField] = verdict.message;
        return false;
      }
      if(verdict.verdict === 'crop'){
        return await this.$refs.confirmDialogue.show({
          title: 'Bildet beskjæres',
          message: verdict.message,
          okButton: 'Last opp likevel',
          cancelButton: 'Velg et annet'
        });
      }
      return true;
    },

    /* ── forhåndsvisning ────────────────────────────────────────────────────────── */

    /**
     * Opens the real ordering page on the merchant's draft.
     *
     * The draft is never fetched by that window — it is posted in, exactly as the Hjemmeside tab posts
     * into its frame. So preview mode adds no endpoint that would hand unpublished work to anyone who
     * knows a merchant slug.
     */
    openPreview() {
      if (!this.storefrontUrl) return;
      const win = window.open(this.storefrontUrl + '?preview=1', 'cagista-ordering-preview');
      if (!win) {
        // A blocked popup is indistinguishable from a broken button: the merchant clicks and nothing
        // happens, with no way to know a browser setting did it rather than us.
        this.previewBlocked = true;
        return;
      }
      this.previewBlocked = false;
      this.previewWindow = win;
    },

    /** Posts the current draft into an open preview window, so it tracks the controls. */
    postToPreview() {
      if (!this.previewWindow || this.previewWindow.closed || !this.previewOrigin) return;
      this.previewWindow.postMessage(
          { source: PREVIEW_MSG, type: 'schema', schema: JSON.parse(JSON.stringify(this.schema)) },
          this.previewOrigin);
    },

    onPreviewMessage(event) {
      if (!this.previewOrigin || event.origin !== this.previewOrigin) return;
      const data = event.data;
      if (!data || data.source !== PREVIEW_MSG) return;
      // The handshake: the page says `ready` once it is mounted and listening. Posting before that
      // would land the schema in a window with no handler.
      // The page names itself in the handshake. `event.source` cannot be used: a cross-origin
      // navigation gives the opened window a new WindowProxy, so it never matches the handle
      // `window.open` returned. Nothing is leaked by trusting the name — the reply is posted to OUR
      // handle at OUR origin, not back at the sender.
      if (data.type === 'ready' && data.mode === 'preview') this.postToPreview();
    },

    /**
     * The logo, which lives on merchant_setting rather than in the site schema.
     *
     * It is deliberately not a schema field. The storefront header renders it on EVERY page — menu,
     * info, cart, checkout — while the schema is only read on the ordering page. A copy in the schema
     * would show one logo there and another everywhere else, which is exactly the split hero.image
     * already suffers.
     */
    loadMerchantSetting(){
      SettingsService.getMerchantSetting().then(
        (res) => {
          this.merchantSetting = res.data || null;
          this.logoImage = (this.merchantSetting && this.merchantSetting.logoImageFile) || '';
          this.logoAtLoad = this.logoImage;
        },
        (err) => {
          console.log(err);
          // Non-fatal: the rest of the builder works. Only the logo card is unusable, and it says so.
          this.logoError = 'Kunne ikke hente logoen. Last siden på nytt for å prøve igjen.';
        }
      );
    },

    async selectLogo(){
      const input = this.$refs.logoFile;
      const file = input && input.files && input.files[0];
      this.logoError = '';
      if(!file) return;
      if(!this.merchantSetting){
        this.logoError = 'Innstillingene er ikke lastet ennå. Prøv igjen om et øyeblikk.';
        input.value = '';
        return;
      }
      if(!await this.acceptImage(file, SiteImageSlots.SLOTS.logo, 'logoError')){
        input.value = '';
        return;
      }
      this.logoUploading = true;
      // Fitted, never cropped: a logo that loses an edge is a broken logo, and its transparent
      // background has to survive — which it does, WebP keeps alpha. Re-encoded either way, because it
      // loads on every page of the ordering site.
      ImageCompressor.compressImageToSlot(file, SiteImageSlots.SLOTS.logo)
        .then((fitted) => fitted.file)
        .catch(() => file)   // an odd file still uploads as-is, as the banner and product pages do
        .then((compressed) => MediaFileService.uploadImageFile(compressed))
        .then((response) => {
          const imageUrl = response && response.data;
          if(imageUrl){
            this.logoImage = imageUrl;
          } else {
            this.logoError = 'Opplastingen returnerte ingen bilde-URL. Prøv igjen.';
          }
          this.logoUploading = false;
          input.value = '';
        })
        .catch((error) => {
          console.log(error);
          this.logoError = 'Kunne ikke laste opp logoen. Prøv igjen.';
          this.logoUploading = false;
          input.value = '';
        });
    },

    removeLogo(){
      this.logoImage = '';
      this.logoError = '';
      const input = this.$refs.logoFile;
      if(input) input.value = '';
    },

    /**
     * Persist the logo, and ONLY if it changed.
     *
     * Sends back the whole document that was loaded with just logoImageFile replaced. Never a freshly
     * built object: editMerchantSetting upserts what it is handed, so a subset would silently drop every
     * other setting on the merchant.
     */
    saveLogo(){
      if(!this.merchantSetting || this.logoImage === this.logoAtLoad){
        return Promise.resolve();
      }
      const doc = Object.assign({}, this.merchantSetting, { logoImageFile: this.logoImage });
      return SettingsService.editMerchantSetting(doc).then(() => {
        this.merchantSetting = doc;
        this.logoAtLoad = this.logoImage;
      });
    },

    /**
     * Upload a hero banner, the same way product photos are uploaded: the browser re-encodes to WebP and
     * posts to /api/media/uploadImageFile, and the returned URL goes into the schema.
     *
     * <b>Cropped to the banner's shape, not refused for missing it.</b> This used to demand exactly
     * 1280 × 400 and reject anything else with the measurements it had — accurate, and a dead end for
     * a restaurant owner holding one photograph of their dining room. The banner renders
     * `object-fit: cover`, so the browser was going to crop it centrally regardless; doing it at upload
     * is simply the version the merchant can see.
     *
     * The URL is only written into `schema.hero.image`; nothing is persisted until «Publiser».
     */
    async selectHeroImage(){
      const input = this.$refs.heroFile;
      const file = input && input.files && input.files[0];
      this.heroError = '';
      if(!file){
        return;
      }
      const slot = SiteImageSlots.SLOTS.orderingHero;
      if(!await this.acceptImage(file, slot, 'heroError')){
        input.value = '';
        return;
      }
      this.heroUploading = true;
      ImageCompressor.compressImageToSlot(file, slot)
        .then((fitted) => fitted.file)
        .catch(() => file)   // an unsupported/odd file still uploads as-is, as the product pages do
        .then((compressed) => MediaFileService.uploadImageFile(compressed))
        .then((response) => {
          const imageUrl = response && response.data;
          if(imageUrl){
            this.schema.hero.image = imageUrl;
          } else {
            this.heroError = 'Opplastingen returnerte ingen bilde-URL. Prøv igjen.';
          }
          this.heroUploading = false;
          input.value = '';
        })
        .catch((error) => {
          console.log(error);
          this.heroError = 'Kunne ikke laste opp bildet. Prøv igjen.';
          this.heroUploading = false;
          input.value = '';
        });
    },
    /**
     * Clears the banner from the schema so the Storefront falls back to the location's own header image.
     * The uploaded file is left in storage on purpose: the schema is versioned, so an older version may
     * still reference it, and deleting the object would break that version's preview.
     */
    removeHeroImage(){
      this.schema.hero.image = '';
      this.heroError = '';
      if(this.$refs.heroFile){
        this.$refs.heroFile.value = '';
      }
    },
    injectFonts() {
      if (document.getElementById('site-builder-fonts')) return;
      const link = document.createElement('link');
      link.id = 'site-builder-fonts';
      link.rel = 'stylesheet';
      link.href = 'https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700&family=Roboto:wght@300;400;500;700&family=Poppins:wght@400;500;600;700&family=Playfair+Display:wght@400;600;700;800&family=Roboto+Mono:wght@400;500;700&display=swap';
      document.head.appendChild(link);
    },
    loadMenu() {
      // Categories + items are merchant-wide; the preview filters them by the selected location.
      ProductService.getCategories().then((res) => { this.categories = res.data || []; }, (e) => { console.log(e); });
      ProductService.getItems().then((res) => { this.items = res.data || []; }, (e) => { console.log(e); });
    },
    loadVersions() {
      if (!this.location) return;
      SiteThemeService.getVersions(this.location).then(
        (res) => { this.versions = res.data || []; },
        (e) => { console.log(e); this.versions = []; }
      );
    },
    /** An overlay has to close the way every other overlay does, or it is a panel that gets stuck. */
    onDocumentClick(event) {
      if (!this.showVersions) return;
      const panel = this.$refs.versions;
      const button = this.$refs.versionsButton;
      if (panel && panel.contains(event.target)) return;
      if (button && button.contains(event.target)) return;
      this.showVersions = false;
    },

    onKeydown(event) {
      if (event.key === "Escape" && this.showVersions) this.showVersions = false;
    },

    restoreVersion(v) {
      if (!v || !v.schema) return;
      // Same race as discard: an autosave in flight would write the current draft back over the
      // version just restored.
      this.cancelQueuedSave();
      this.applySchema(v.schema);
      this.showVersions = false;
      // It IS kept — the draft is saved from here on. What it is not yet is published.
      this.genNote = 'Hentet tilbake versjon ' + v.version + '. Trykk Publiser for å gjøre den synlig for gjestene.';
    },
    formatTime(iso) {
      if (!iso) return '';
      const d = new Date(iso);
      if (isNaN(d.getTime())) return '';
      return d.toLocaleDateString('nb-NO') + ' ' + d.toLocaleTimeString('nb-NO', { hour: '2-digit', minute: '2-digit' });
    },
    availAt(e) {
      const ids = e && e.availableAtLocationIds;
      if (!ids || !ids.length) return true; // no restriction = available everywhere
      return this.locId ? ids.indexOf(this.locId) >= 0 : true;
    },
    // Only what shows on the website/storefront: the web channels — "Takeaway på Web" (WEB_TAKEAWAY)
    // or "QR-bestilling ved bord" (WEB_DINE_IN). Missing/empty = all channels (matches old-data handling).
    availForWeb(e) {
      const ids = e && e.availableForChannelIds;
      if (!ids || !ids.length) return true;
      return ids.indexOf('WEB_TAKEAWAY') >= 0 || ids.indexOf('WEB_DINE_IN') >= 0;
    },
    setActiveCat(key) {
      this.previewCategoryId = key;
    },
    onLocationChange() {
      this.previewCategoryId = ''; // reset to the new location's first category
      this.savedInfo = false;
      this.loadSchema();
    },
    /**
     * Adopt a schema that came from the SERVER — first load, a restored version, a discarded draft.
     *
     * The deep watch above cannot tell those from a merchant moving a slider, and answering a read
     * with a write is how «Forkast endringer» would undo itself: the discard returns the published
     * page, the watch queues a save, and the draft everyone just agreed to delete is written back a
     * second later. The Hjemmeside tab avoids this by calling queueSave at each edit by hand; here
     * one flag around the one adoption point does the same job.
     *
     * Cleared on nextTick, which runs after the pre-flush watcher queue.
     */
    applySchema(s) {
      this.suppressSave = true;
      this.$nextTick(() => { this.suppressSave = false; });
      const d = defaultSchema();
      this.schema = {
        version: s && s.version ? s.version : 1,
        theme: Object.assign({}, d.theme, (s && s.theme) || {}),
        hero: Object.assign({}, d.hero, (s && s.hero) || {}),
        about: Object.assign({}, d.about, (s && s.about) || {}),
        mapUrl: (s && typeof s.mapUrl === 'string') ? s.mapUrl : '',
        menuLayout: (s && ['cards', 'gallery', 'list', 'sidebar'].includes(s.menuLayout)) ? s.menuLayout : 'cards',
        sections: (s && Array.isArray(s.sections) && s.sections.filter((x) => ALLOWED_SECTIONS.includes(x)).length)
          ? s.sections.filter((x) => ALLOWED_SECTIONS.includes(x))
          : d.sections.slice()
      };
    },
    loadSchema() {
      if (!this.location) { this.loading = false; return; }
      this.loading = true;
      this.genNote = '';
      this.loadVersions();
      // Both at once. The draft is what gets edited — the same order the Hjemmeside tab uses, so
      // unpublished work is still there when a merchant comes back to it — but the published version
      // is needed either way, or «Forkast endringer» sits disabled for a merchant who has one.
      Promise.all([
        SiteThemeService.getSiteSchemaDraft(this.location),
        SiteThemeService.getSiteSchema(this.location)
      ]).then(
        ([draftRes, liveRes]) => {
          const draft = draftRes.data && draftRes.data.schema;
          const live = liveRes.data;
          this.publishedVersion = (live && live.version) || 0;
          this.storefrontBase = (live && live.storefrontUrl) || '';
          this.applySchema(draft || (live && live.schema) || null);
          this.loading = false;
        },
        (err) => { console.log(err); this.applySchema(null); this.loading = false; }
      );
    },
    generate() {
      if (!this.prompt.trim() || this.generating) return;
      this.generating = true;
      this.genNote = '';
      SiteThemeService.generate(this.prompt, this.schema).then(
        (res) => {
          if (res.data && res.data.schema) this.applySchema(res.data.schema);
          this.genNote = (res.data && res.data.note === 'no-change')
            ? 'Ingen gjenkjent stil — prøv ord som «mørk», «elegant», «grønn», «avrundet».'
            : 'Brukt ✓';
          this.generating = false;
        },
        (err) => { console.log(err); this.genNote = 'Generering feilet.'; this.generating = false; }
      );
    },
    /**
     * Makes it live.
     *
     * <b>«Publiser», not «Lagre».</b> The two halves of Website Builder used two different mental
     * models — the homepage «nothing is live until you publish», this page «every save is live» — two
     * tabs apart in one editor. The safer model won, and the word had to follow it.
     */
    publish() {
      if (!this.location) return;
      // Redundant, and a race: publish carries the schema itself, so a queued autosave could land
      // after it and write an older draft over what was just published.
      this.cancelQueuedSave();
      this.saving = true;
      this.statusError = false;
      this.savedInfo = false;
      // The logo goes with the same button. It is a different document, so it is a separate call — but
      // two save buttons on one page is how a merchant loses work they thought they had saved.
      SiteThemeService.publishSiteSchema(this.location, this.schema).then(
        (res) => {
          if (res.data && res.data.version) {
            this.schema.version = res.data.version;
            this.publishedVersion = res.data.version;
          }
          this.loadVersions();
          return this.saveLogo();
        }
      ).then(
        () => {
          this.status = 'Publisert ✓';
          this.savedInfo = true;
          setTimeout(() => (this.status = ''), 2500);
          this.saving = false;
        },
        (err) => { console.log(err); this.statusError = true; this.status = 'Publisering feilet'; setTimeout(() => (this.status = ''), 3500); this.saving = false; }
      );
    },

    queueSave() {
      if (this.suppressSave || this.loading || !this.location) return;
      if (this.saveTimer) clearTimeout(this.saveTimer);
      this.saveTimer = setTimeout(this.saveDraft, AUTOSAVE_MS);
    },

    saveDraft() {
      this.saveTimer = null;
      if (!this.location || !this.schema) return;
      this.draftSaving = true;
      SiteThemeService.saveSiteSchema(this.location, this.schema).then(
        () => {
          this.draftSaving = false;
          this.savedAt = new Date().toLocaleTimeString('nb-NO', { hour: '2-digit', minute: '2-digit' });
        },
        (err) => { console.log(err); this.draftSaving = false; }
      );
    },

    /** A queued autosave carries the schema being replaced — cancel it before anything replaces one. */
    cancelQueuedSave() {
      if (this.saveTimer) { clearTimeout(this.saveTimer); this.saveTimer = null; }
    },

    /** Back to what is live. Same control, same wording, as the Hjemmeside tab. */
    async discardDraft() {
      if (!this.location) return;
      const ok = await this.$refs.confirmDialogue.show({
        title: 'Forkast endringer',
        message: 'Utkastet ditt slettes og du starter fra den publiserte bestillingssiden igjen.',
        okButton: 'Forkast',
        cancelButton: 'Avbryt'
      });
      if (!ok) return;
      // Cancel FIRST. A save queued before this call carries the very schema being thrown away and
      // would land a second later, silently putting it back — the discard appears to work and then
      // undoes itself.
      this.cancelQueuedSave();
      SiteThemeService.discardDraft(this.location).then(
        (res) => {
          if (res.data && res.data.schema) this.applySchema(res.data.schema);
          this.status = 'Forkastet ✓';
          setTimeout(() => (this.status = ''), 2500);
        },
        (err) => { console.log(err); this.statusError = true; this.status = 'Kunne ikke forkaste'; setTimeout(() => (this.status = ''), 3500); }
      );
    },
    applyLayout(p) {
      // Layout presets only reorder/select sections — theme, hero copy and about content are kept (an
      // "Om oss" section that's dropped is just hidden; its text stays in schema.about for later).
      this.schema.sections = p.sections.slice();
      this.status = 'Oppsett valgt: ' + p.label + ' ✓';
      this.statusError = false;
      setTimeout(() => (this.status = ''), 2500);
    },
    sectionShort(s) {
      return { hero: 'Topp', menu: 'Meny', about: 'Om oss' }[s] || s;
    },
    applyMenuLayout(key) {
      this.schema.menuLayout = key;
      const label = (this.menuLayouts.find((m) => m.key === key) || {}).label || key;
      this.status = 'Menyvisning: ' + label + ' ✓';
      this.statusError = false;
      setTimeout(() => (this.status = ''), 2500);
    },
    mldCells(key) {
      return { cards: 2, gallery: 4, list: 3, sidebar: 3 }[key] || 3;
    },
    async resetSchema() {
      const ok = await this.$refs.confirmDialogue.show({
        title: 'Tilbakestill tema',
        message: 'Tilbakestille temaet til standard? Ulagrede endringer går tapt.',
        okButton: 'Tilbakestill',
        cancelButton: 'Avbryt'
      });
      if (!ok) return;
      this.applySchema(defaultSchema());
      this.prompt = '';
      this.genNote = '';
      this.statusError = false;
      this.status = 'Tilbakestilt til standard ✓';
      setTimeout(() => (this.status = ''), 2500);
    },
    isRequired(s) { return REQUIRED_SECTIONS.includes(s); },
    moveSection(i, dir) {
      const j = i + dir;
      if (j < 0 || j >= this.schema.sections.length) return;
      const arr = this.schema.sections;
      const tmp = arr[i]; arr[i] = arr[j]; arr[j] = tmp;
    },
    removeSection(i) {
      if (this.isRequired(this.schema.sections[i])) return;
      this.schema.sections.splice(i, 1);
    },
    addSection(s) {
      if (!this.schema.sections.includes(s)) this.schema.sections.push(s);
    }
  }
};
</script>

<style scoped>
/* Match the /overview page content width */
.sb-page { max-width: 1860px; margin: 0 auto; padding: 24px; }

/* Action panel: buttons on one row, status on a reserved line beneath. The column's width is set by the
   button row — the longest status («Oppsett valgt: Om oss først ✓») is well short of it — so a message
   can never widen the column and make the header row wrap. */
.sb-actions { display: flex; flex-direction: column; align-items: flex-end; }
.sb-actions__row { display: flex; align-items: center; gap: 0.75rem; }
/* Same treatment as the Hjemmeside tab's: its own line under the buttons, so a blocked popup reports
   itself without reflowing the row it was clicked in. */
.sb-actions__blocked {
  flex-basis: 100%;
  text-align: right;
  font-size: .8rem;
  color: var(--bo-bad-fg);
}

.sb-actions__status--saved { color: var(--bo-muted); }

.sb-actions__status {
  min-height: 1.15rem;   /* holds the line open when empty, so nothing moves when a message appears */
  margin-top: 0.3rem;
  font-size: 0.85rem;
  white-space: nowrap;   /* a two-line status would change the height the min-height is reserving */
}

.bb-published { display: flex; align-items: center; justify-content: space-between; flex-wrap: wrap; gap: 0.75rem; margin-top: 1rem; padding: 0.7rem 1rem; background: var(--bo-ok-bg); border: 1px solid #b7e4c7; border-radius: 8px; color: #197d2b; }
/* Logo preview: contained, never stretched — a logo has its own aspect ratio and cropping it is worse
   than showing it small. The chequer makes a transparent PNG readable on either theme. */
.bb-logo-preview {
  margin: 0.5rem 0;
  padding: 0.5rem;
  border: 1px solid var(--bo-line);
  border-radius: 6px;
  background-color: var(--bo-surface-2);
  background-image:
    linear-gradient(45deg, var(--bo-line) 25%, transparent 25%, transparent 75%, var(--bo-line) 75%),
    linear-gradient(45deg, var(--bo-line) 25%, transparent 25%, transparent 75%, var(--bo-line) 75%);
  background-size: 14px 14px;
  background-position: 0 0, 7px 7px;
  display: inline-block;
}
.bb-logo-preview img { display: block; max-width: 200px; max-height: 80px; object-fit: contain; }

.bb-card { border: 1px solid var(--bo-line); border-radius: 10px; padding: 0.85rem 1rem; margin-bottom: 1rem; background: var(--bo-surface); }
.bb-h { font-size: 0.75rem; text-transform: uppercase; letter-spacing: 0.04em; color: var(--bo-muted); margin-bottom: 0.75rem; }
.bb-row { display: flex; align-items: center; justify-content: space-between; gap: 0.75rem; margin-bottom: 0.55rem; font-size: 0.9rem; }
.bb-color { width: 48px; height: 30px; padding: 0; border: 1px solid var(--bo-muted-2); border-radius: 6px; background: none; }
.bb-sel { width: auto; min-width: 150px; }

/* Hero banner upload */
.bb-sublabel { font-size: 0.8rem; color: var(--bo-muted); margin-bottom: 0.4rem; }
.bb-btn-sm { min-width: auto; font-size: 0.82rem; padding: 4px 12px; }
.bb-hero-msg { font-size: 0.8rem; color: var(--bo-muted); margin-top: 0.4rem; }
.bb-hero-msg--error { color: var(--bo-alert-ink); }
.bb-hero-msg--ok { color: var(--bo-success-ink); }

/* Shared by the Oppsett and Menyvisning tiles. It used to live with the theme swatches and came out
   with them; without it both sets of labels lose their size and colour. */
.bb-preset-label { font-size: 0.8rem; line-height: 1.15; color: var(--bo-ink); }

/* Layout presets ("Oppsett") — a tiny stacked diagram of the section arrangement */
.bb-layouts { display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 0.5rem; }
.bb-layout { display: flex; flex-direction: column; align-items: center; gap: 0.4rem; padding: 0.5rem 0.35rem; background: var(--bo-surface); border: 1px solid var(--bo-line); border-radius: 8px; cursor: pointer; transition: all .2s ease; }
.bb-layout:hover { border-color: #c9c9cd; transform: translateY(-1px); }
.bb-layout.active { border-color: #ECB109; box-shadow: 0 0 0 1px #ECB109; }
.bb-layout-diagram { display: flex; flex-direction: column; gap: 2px; width: 100%; }
.bb-lrow { display: flex; align-items: center; justify-content: center; font-size: 7px; font-style: normal; text-transform: uppercase; letter-spacing: 0.02em; color: #fff; border-radius: 2px; overflow: hidden; }
.bb-lrow.lr-hero { height: 15px; background: var(--bo-muted); }
.bb-lrow.lr-menu { height: 22px; background: var(--bo-ink); }
.bb-lrow.lr-about { height: 14px; background: var(--bo-muted-2); }
.bb-layout .bb-preset-label { text-align: center; }

/* Menu-layout mini diagram (Menyvisning tiles): cards = 2 blocks, gallery = 2×2, list = 3 rows */
.bb-mld { display: grid; gap: 2px; width: 100%; height: 30px; }
.bb-mld i { background: #cfd2d6; border-radius: 2px; }
.bb-mld-cards { grid-template-columns: 1fr 1fr; }
.bb-mld-gallery { grid-template-columns: 1fr 1fr; grid-auto-rows: 1fr; }
.bb-mld-list { grid-template-columns: 1fr; grid-auto-rows: 1fr; }
.bb-mld-sidebar { grid-template-columns: 1fr 1.8fr; grid-template-rows: 1fr 1fr; }
.bb-mld-sidebar i:first-child { grid-row: 1 / -1; }
.bb-layout.active .bb-mld i { background: #ECB109; }
.bb-range { width: 130px; }
.bb-sections { list-style: none; padding: 0; margin: 0; display: flex; flex-direction: column; gap: 0.35rem; }
.bb-sections li { display: flex; align-items: center; justify-content: space-between; background: var(--bo-surface-2); border: 1px solid var(--bo-line); border-radius: 8px; padding: 0.35rem 0.6rem; font-size: 0.9rem; }
.bb-mini { width: 30px; padding: 0.1rem 0; margin-left: 0.2rem; }
.bb-versions { list-style: none; padding: 0; margin: 0; max-height: 220px; overflow-y: auto; display: flex; flex-direction: column; gap: 0.35rem; }
.bb-versions li { display: flex; align-items: center; justify-content: space-between; gap: 0.5rem; font-size: 0.85rem; }
.bb-ver-meta { color: var(--bo-ink-2); }

/* Preview themed by the schema via CSS vars — mirrors the standard Storefront ordering layout */
.preview-frame { background: var(--bg); color: var(--text); font-family: var(--font); border: 1px solid var(--bo-line); border-radius: 14px; overflow: hidden; min-height: 70vh; }
.preview-frame h1, .preview-frame h2, .preview-frame h3 { font-weight: var(--hw); }
.pv-ico { width: 17px; height: 17px; fill: none; stroke: currentColor; stroke-width: 1.8; stroke-linecap: round; stroke-linejoin: round; flex: 0 0 auto; }

/* Hero — full-bleed image banner */
.pv-hero { aspect-ratio: 1280 / 400; background: linear-gradient(135deg, var(--border), var(--surface)); background-size: cover; background-position: center; display: flex; align-items: flex-end; }
.pv-hero-text { padding: 1.5rem 2rem; }
.pv-hero-text h1 { font-size: 2rem; line-height: 1.1; margin: 0 0 0.4rem; color: #fff; text-shadow: 0 1px 10px rgba(0,0,0,.55); }
.pv-hero-text p { color: var(--bo-surface-2); font-size: 1rem; margin: 0 0 0.9rem; text-shadow: 0 1px 8px rgba(0,0,0,.55); }

/* Store header bar (logo · name/open · phone/prep · Meny/Info/Handlekurv) */
.pv-header { display: flex; align-items: center; gap: 1.25rem; padding: 0.85rem 1.5rem; background: var(--bg); border-bottom: 1px solid var(--border); flex-wrap: wrap; }
.pv-logo { width: 92px; height: 46px; border-radius: 24px; background: var(--bo-surface); display: flex; align-items: center; justify-content: center; flex: 0 0 auto; padding: 4px 8px; overflow: hidden; }
.pv-logo img { max-width: 100%; max-height: 100%; object-fit: contain; }
.pv-store-block { min-width: 130px; }
.pv-store-name { font-weight: 700; font-size: 1.05rem; }
.pv-store-sub { font-size: 0.8rem; color: var(--muted); display: flex; align-items: center; gap: 0.3rem; }
.pv-open { color: #35c759; font-weight: 600; }
.pv-dim { color: var(--muted); }
.pv-contact { font-size: 0.82rem; display: flex; flex-direction: column; gap: 0.25rem; }
.pv-contact-row { display: flex; align-items: center; gap: 0.4rem; }
.pv-topnav { display: flex; gap: 3.2rem; margin-left: auto; }
.pv-tab { display: flex; flex-direction: column; align-items: center; gap: 0.2rem; font-size: 0.78rem; color: var(--muted); cursor: pointer; position: relative; }
.pv-tab.active { color: var(--text); }
.pv-tab.active::after { content: ''; position: absolute; bottom: -5px; left: 0; right: 0; height: 2px; background: var(--primary); }
.pv-cart { position: relative; display: inline-flex; width: 34px; height: 34px; border-radius: 50%; background: var(--primary); align-items: center; justify-content: center; color: var(--on-primary); }
.pv-cart .pv-ico { width: 18px; height: 18px; }
.pv-cart-badge { position: absolute; top: -3px; right: -3px; background: var(--bo-surface-2); color: var(--primary); font-size: 0.6rem; font-style: normal; font-weight: 700; min-width: 15px; height: 15px; padding: 0 3px; border-radius: 8px; display: flex; align-items: center; justify-content: center; }

/* Category nav — white bar */
.pv-cats { display: flex; gap: 1.5rem; overflow-x: auto; padding: 0.75rem 1.5rem; background: var(--bo-surface); }
.pv-cat { font-size: 0.85rem; font-weight: 700; text-transform: uppercase; letter-spacing: .02em; color: var(--bo-ink); white-space: nowrap; cursor: pointer; }
.pv-cat.active { color: var(--primary); }

/* Menu grid — dark, 2-column horizontal cards */
.pv-menu-body { padding: 1.5rem; }
.pv-menu-head { display: flex; align-items: center; justify-content: space-between; margin-bottom: 1rem; }
.pv-menu-head h2 { font-size: 1.1rem; text-transform: uppercase; letter-spacing: .02em; margin: 0; }
.pv-allergen { font-size: 0.85rem; color: var(--muted); display: flex; align-items: center; gap: 0.5rem; cursor: pointer; margin: 0; }
.pv-switch { width: 38px; height: 20px; border-radius: 20px; background: var(--bo-muted); position: relative; transition: background .2s; flex: 0 0 auto; }
.pv-switch i { position: absolute; top: 2px; left: 2px; width: 16px; height: 16px; border-radius: 50%; background: var(--bo-surface); transition: left .2s; }
.pv-switch.on { background: var(--primary); }
.pv-switch.on i { left: 20px; }
.pv-grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 1rem; }
.pv-card { display: flex; align-items: center; gap: 1rem; background: var(--surface); border: 1px solid var(--border); border-radius: var(--radius); padding: 0.9rem; }
.pv-thumb { width: 88px; height: 88px; flex: 0 0 auto; border-radius: calc(var(--radius) * 0.8); background: linear-gradient(135deg, var(--border), var(--surface)); background-size: cover; background-position: center; }
.pv-card-body { flex: 1 1 auto; min-width: 0; }
.pv-card-body h3 { font-size: 0.95rem; text-transform: uppercase; margin: 0 0 0.25rem; }
.pv-muted { color: var(--muted); font-size: 0.82rem; margin: 0 0 0.3rem; }
.pv-allergens { color: #f30b0c; font-size: 0.8rem; margin: 0 0 0.3rem; }
.pv-price { color: var(--primary); font-weight: 700; font-size: 0.95rem; }
.pv-add { flex: 0 0 auto; width: 38px; height: 38px; border-radius: 50%; border: 2px solid var(--primary); background: transparent; color: var(--primary); font-size: 1.35rem; line-height: 1; font-weight: 400; cursor: pointer; display: flex; align-items: center; justify-content: center; }

/* Menu-layout preview variants (schema.menuLayout) — mirror the Storefront site-menu--* rules */
.pv-grid--gallery { grid-template-columns: repeat(3, 1fr); }
.pv-grid--gallery .pv-card { flex-direction: column; align-items: stretch; }
.pv-grid--gallery .pv-thumb { width: 100%; height: 130px; }
.pv-grid--gallery .pv-add { align-self: flex-end; margin-top: -2.6rem; }
.pv-grid--list { grid-template-columns: 1fr; }
.pv-grid--list .pv-card { padding: 0.6rem 0.9rem 1.1rem; gap: 1.5rem; }
.pv-grid--list .pv-thumb { width: 120px; height: 120px; }

/* Sidebar — category nav as a left column, menu on the right (preview mirror of site-menu--sidebar) */
.pv-menu--sidebar { display: grid; grid-template-columns: 130px 1fr; column-gap: 1rem; row-gap: 1.1rem; }
.pv-menu--sidebar .pv-header { grid-column: 1 / -1; }
.pv-menu--sidebar .pv-cats { grid-column: 1; flex-direction: column; align-items: flex-start; gap: 0.4rem; overflow: visible; padding: 0.75rem 0 0.75rem 1.5rem; white-space: normal; }
.pv-menu--sidebar .pv-menu-body { grid-column: 2; }
.pv-menu--sidebar .pv-grid { grid-template-columns: 1fr; }

/* About */
.pv-about { padding: 1.75rem 1.5rem; border-top: 1px solid var(--border); }
.pv-about h2 { font-size: 1.3rem; margin-bottom: 0.8rem; }
</style>

<style scoped>
/* The pointer to where the look now lives: present, but not competing with the controls that do
   something on this tab. */
.bb-card--muted { opacity: .85; }
.bb-note { font-size: .82rem; color: var(--bo-muted); margin: 0; }
</style>

<style scoped>
/* The version history, as an overlay hung off its own button — the same card the Hjemmeside tab uses,
   so the two halves of one editor do not present the same thing two different ways. */
.sb-actions__row { position: relative; }

.sb-versions {
  position: absolute;
  top: calc(100% + 8px);
  right: 0;
  z-index: 30;
  width: 480px;
  max-width: calc(100vw - 32px);
  text-align: left;
  background: var(--bo-surface);
  border: 1px solid var(--bo-line);
  border-radius: 8px;
  padding: 14px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, .18);
}

.sb-versions__title { font-size: .95rem; margin-bottom: 4px; }
.sb-versions__hint { font-size: .8rem; color: var(--bo-muted); margin-bottom: 10px; }

.sb-versions__list {
  list-style: none;
  margin: 0;
  padding: 0;
  /* Twenty entries, capped: unbounded it would run off the screen. */
  max-height: 260px;
  overflow-y: auto;
}

.sb-versions__list li {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 7px 0;
  border-top: 1px solid var(--bo-line);
}

.sb-versions__list li:first-child { border-top: 0; }
.sb-versions__no { font-size: .9rem; white-space: nowrap; }
.sb-versions__when { flex: 1 1 auto; font-size: .8rem; color: var(--bo-muted); }

@media (max-width: 991px) {
  .sb-versions { width: auto; left: 0; right: 0; }
}
</style>
